'use client'

import React, { useEffect, useMemo, useState } from 'react'
import SamanthaReveal from './samantha-reveal'
import { useTheme } from 'next-themes'
import { useSellerId } from '@/hooks/useSellerId'
import { usePathname } from 'next/navigation'

type MasterAgent = {
  id: number
  status?: string | null
  is_trained?: number | boolean | null
  is_master?: number | boolean | null
  name?: string | null
}

type Props = {
  user?: {
    customer_id?: number | string | null
    seller_id?: number | string | null
    firstname?: string | null
    name?: string | null
  } | null
  enabled?: boolean
}

export default function SamanthaRevealProvider({ user, enabled = true }: Props) {
  const { resolvedTheme } = useTheme()
  const { sellerId } = useSellerId()

  const [open, setOpen] = useState(false)
  const [ready, setReady] = useState(false)
  const [checkedStatus, setCheckedStatus] = useState(false)
  const [experienceEnabled, setExperienceEnabled] = useState<boolean | null>(null)

  const [masterAgent, setMasterAgent] = useState<MasterAgent | null>(null)
  const [hasLoadedMaster, setHasLoadedMaster] = useState(false)

  const pathname = usePathname()

  const hiddenPrefixes = [
    '/login',
    '/start',
    '/activate',
    '/funnel-view',
    '/funnel-preview',
    '/product',
  ]

  const isBlockedPath =
    !pathname ||
    pathname === '/' ||
    hiddenPrefixes.some((p) => pathname === p || pathname.startsWith(p + '/'))

  const storageKey = useMemo(() => {
    if (!sellerId) return null
    return `hubee:samantha_reveal_seen:${sellerId}:v1`
  }, [sellerId])

  useEffect(() => {
    let cancelled = false

    async function loadExperienceConfig() {
      try {
        const res = await fetch('/api/settings', {
          cache: 'no-store',
          credentials: 'include',
        })

        const data = await res.json().catch(() => null)

        if (cancelled) return

        if (res.ok) {
          setExperienceEnabled(
            data?.experience?.enabled == null ? true : !!data.experience.enabled
          )
        } else {
          setExperienceEnabled(true)
        }
      } catch (err) {
        console.error('[SamanthaRevealProvider] failed to load experience config:', err)

        if (!cancelled) {
          setExperienceEnabled(true)
        }
      }
    }

    loadExperienceConfig()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!sellerId) {
      setMasterAgent(null)
      setHasLoadedMaster(false)
      return
    }

    let cancelled = false

    async function loadMasterAgent() {
      try {
        const res = await fetch(`/api/ai/agents/${sellerId}/master`, {
          cache: 'no-store',
        })

        if (!res.ok) {
          if (!cancelled) {
            setMasterAgent(null)
            setHasLoadedMaster(true)
          }
          return
        }

        const agent = await res.json()

        if (cancelled) return

        setMasterAgent(agent || null)
        setHasLoadedMaster(true)
      } catch (err) {
        console.error('[SamanthaRevealProvider] failed to load master agent:', err)

        if (!cancelled) {
          setMasterAgent(null)
          setHasLoadedMaster(true)
        }
      }
    }

    loadMasterAgent()

    return () => {
      cancelled = true
    }
  }, [sellerId])

  const isMasterReady = useMemo(() => {
    if (!masterAgent) return false

    const isTrained =
      Number(masterAgent?.is_trained || 0) === 1 ||
      masterAgent?.is_trained === true

    const isActive =
      String(masterAgent?.status || '').trim().toLowerCase() === 'active'

    return isTrained && isActive
  }, [masterAgent])

  const userName = useMemo(() => {
    const first = String(user?.firstname || '').trim()
    if (first) return first

    const full = String(user?.name || '').trim()
    if (full) return full.split(' ')[0]

    return '...'
  }, [user])

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 600)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    let cancelled = false
    let launchTimer: ReturnType<typeof setTimeout> | null = null

    async function checkStatus() {
      if (!enabled || !ready) return
      if (experienceEnabled === null) return

      if (experienceEnabled === false) {
        setCheckedStatus(true)
        return
      }

      if (!user?.customer_id || !sellerId) return
      if (!hasLoadedMaster) return
      if (!isMasterReady) {
        setCheckedStatus(true)
        return
      }

      if (!storageKey) {
        setCheckedStatus(true)
        return
      }

      const localSeen = window.localStorage.getItem(storageKey)
      if (localSeen === '1') {
        setCheckedStatus(true)
        return
      }

      try {
        const res = await fetch(
          `/api/ai/samantha/reveal/status?seller_id=${encodeURIComponent(String(sellerId))}`,
          { cache: 'no-store' }
        )

        const data = await res.json().catch(() => null)
        if (cancelled) return

        if (data?.ok && data?.viewed === true) {
          window.localStorage.setItem(storageKey, '1')
          setCheckedStatus(true)
          return
        }

        launchTimer = setTimeout(() => {
          if (cancelled) return
          setOpen(true)
          document.documentElement.style.overflow = 'hidden'
          document.body.style.overflow = 'hidden'
          setCheckedStatus(true)
        }, 900)
      } catch {
        if (cancelled) return

        launchTimer = setTimeout(() => {
          if (cancelled) return
          setOpen(true)
          document.documentElement.style.overflow = 'hidden'
          document.body.style.overflow = 'hidden'
          setCheckedStatus(true)
        }, 900)
      }
    }

    checkStatus()

    return () => {
      cancelled = true
      if (launchTimer) clearTimeout(launchTimer)
    }
  }, [
    enabled,
    ready,
    experienceEnabled,
    user?.customer_id,
    sellerId,
    hasLoadedMaster,
    isMasterReady,
    storageKey,
  ])

  useEffect(() => {
    return () => {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
    }
  }, [])

  async function close() {
    setOpen(false)

    if (storageKey) {
      window.localStorage.setItem(storageKey, '1')
    }

    document.documentElement.style.overflow = ''
    document.body.style.overflow = ''

    if (sellerId) {
      try {
        await fetch('/api/ai/samantha/reveal/status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ seller_id: sellerId }),
        })
      } catch {
        // silent
      }
    }
  }

  async function claimBonus() {
    try {
      await fetch('/api/ai/samantha/reveal/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'first_reveal' }),
      })
    } catch {
      // silent
    }
  }

  function requestAudio() {
    // window.dispatchEvent(new CustomEvent('samantha:audio-requested'))
  }

  function openChat() {
    window.dispatchEvent(
      new CustomEvent('samantha:open-chat', {
        detail: {
          starter: {
            message:
              'Hola. Ya estoy aquí contigo.\n\n¿Quieres crear algo nuevo, mejorar algo que ya tienes o explorar una idea?',
            suggestions: [
              'Crear algo nuevo',
              'Mejorar algo existente',
              'Analizar mi negocio',
              'Solo explorar ideas',
            ],
          },
        },
      })
    )
  }

  if (isBlockedPath) return null
  if (experienceEnabled === null) return null
  if (experienceEnabled === false) return null
  if (!hasLoadedMaster) return null
  if (!isMasterReady) return null
  if (!checkedStatus && !open) return null

  return (
    <SamanthaReveal
      open={open}
      theme={resolvedTheme === 'light' ? 'light' : 'dark'}
      userName={userName}
      bonusCredits={20}
      onClose={close}
      onActivateAudio={requestAudio}
      onContinueWithoutAudio={() => {}}
      onClaimBonus={claimBonus}
      onOpenChat={openChat}
    />
  )
}